import React from "react";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { API_business_settings } from "../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import RightworkSelect from "../../../components/shared/RightworkSelect";

const inputClass = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-600 disabled:bg-slate-100";

function timeLabel(value) {
  const minutes = Math.round((value % 24) * 60);
  const hours = Math.floor(minutes / 60);
  return `${hours % 12 || 12}:${String(minutes % 60).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}${value >= 48 ? " (+2 days)" : value >= 24 ? " (next day)" : ""}`;
}

export function settingsChanges(fields, draft, original) {
  const changes = {};
  fields.forEach((field) => {
    let value = draft[field.key];
    if (field.type === "integer-list" && typeof value === "string") {
      value = value.trim() ? value.split(",").map((part) => part.trim() === "" ? NaN : Number(part.trim())) : [];
      if (value.some((number) => !Number.isInteger(number))) throw new Error(`${field.label}: enter comma-separated whole numbers.`);
    }
    if (["number", "hour"].includes(field.type)) {
      const empty = value === "" || value == null;
      value = empty ? (field.nullable ? null : NaN) : Number(value);
      if (value !== null && !Number.isFinite(value)) throw new Error(`${field.label}: enter a number.`);
    }
    if (["text", "time"].includes(field.type) && value === "" && field.nullable) value = null;
    if (JSON.stringify(value) !== JSON.stringify(original[field.key])) changes[field.key] = value;
  });
  return changes;
}

function errorMessage(error) {
  return error?.response?.data?.system?.message || error?.response?.data?.detail || error.message || "Couldn't save settings. Try again.";
}

export default function BusinessSettingsEditor({ compact = false, group, companyId, storeId, onSaved, onDirtyChange }) {
  const compactDetails = compact && group === "location-details";
  const formId = React.useId();
  const [data, setData] = React.useState(null);
  const [draft, setDraft] = React.useState({});
  const [error, setError] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [reload, setReload] = React.useState(0);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    getRightworkAPI(API_business_settings, { group, company_id: companyId, ...(storeId ? { store_id: storeId } : {}) })
      .then((response) => {
        if (!active) return;
        setData(response.data);
        setDraft(response.data.settings);
        setFieldErrors({});
        onDirtyChange(false);
      })
      .catch((error) => { if (active) setError(errorMessage(error)); });
    return () => { active = false; };
  }, [group, companyId, storeId, reload, onDirtyChange]);

  const change = (key, value) => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    setSaved(false);
    try { onDirtyChange(Object.keys(settingsChanges(data.fields, next, data.settings)).length > 0); }
    catch (_) { onDirtyChange(true); }
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});
    try {
      const changes = settingsChanges(data.fields, draft, data.settings);
      const response = await postRightworkAPI(API_business_settings, {
        group, company_id: companyId, ...(storeId ? { store_id: storeId } : {}),
        settings: changes, expected: data.settings,
      });
      if (!mounted.current) return;
      setData(response.data);
      setDraft(response.data.settings);
      onDirtyChange(false);
      setSaved(true);
      await onSaved?.();
    } catch (error) {
      if (!mounted.current) return;
      setFieldErrors(error?.response?.data?.errors || {});
      setError(errorMessage(error));
    } finally { if (mounted.current) setSaving(false); }
  };

  if (!data) return <div className="p-8" role="status">{error ? <><p className="text-rose-700">{error}</p><button className="mt-4 text-violet-700 underline" onClick={() => setReload((value) => value + 1)}>Try again</button></> : <div className="flex justify-center py-4"><span className="sr-only">Loading settings</span><div aria-hidden="true"><LoadSpinner /></div></div>}</div>;

  return (
    <form onSubmit={save} className={compact ? "py-2" : "mx-auto max-w-3xl px-8 py-6 sm:px-4"}>
      <fieldset disabled={saving} className={compactDetails ? "space-y-4" : "space-y-7"}>
        {data.fields.map((field) => {
          const id = `setting-${formId}-${field.key}`;
          const value = draft[field.key] ?? "";
          const accessibility = { id, "aria-describedby": `${id}-help${fieldErrors[field.key] ? ` ${id}-error` : ""}`, "aria-invalid": Boolean(fieldErrors[field.key]) };
          let control;
          if (field.type === "boolean") {
            control = <input {...accessibility} type="checkbox" checked={Boolean(value)} onChange={(event) => change(field.key, event.target.checked)} className="h-5 w-5 accent-violet-700 focus:ring-2 focus:ring-violet-600" />;
          } else if (field.type === "multiselect") {
            control = <div id={id} role="group" aria-label={field.label} className="flex flex-wrap gap-3">{field.choices.map(([option, label]) => <label key={option} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"><input type="checkbox" className="accent-violet-700" checked={(value || []).includes(option)} onChange={(event) => change(field.key, event.target.checked ? [...(value || []), option].sort() : value.filter((item) => item !== option))} />{label}</label>)}</div>;
          } else if (field.type === "select" || field.type === "hour") {
            const options = field.choices || Array.from({ length: Math.round((field.max - field.min) / field.step) + 1 }, (_, i) => { const hour = field.min + i * field.step; return [hour, timeLabel(hour)]; });
            control = <RightworkSelect id={id} describedBy={accessibility["aria-describedby"]} error={Boolean(fieldErrors[field.key])} disabled={saving}
              value={value} onChange={(event) => change(field.key, event.target.value)}
              options={[...(field.nullable ? [{ value: "", label: "Automatic" }] : []), ...options.map(([option, label]) => ({ value: option, label }))]} />;
          } else {
            control = <input {...accessibility} className={inputClass} type={field.type === "integer-list" ? "text" : field.type} value={Array.isArray(value) ? value.join(", ") : value} onChange={(event) => change(field.key, event.target.value)} min={field.min} max={field.max} step={field.step} maxLength={field.maxLength} required={!field.nullable && field.type !== "integer-list"} />;
          }
          return <div key={field.key}>
            <label id={`${id}-label`} htmlFor={id} className={compactDetails ? "mb-1.5 block text-xs font-medium text-slate-600" : "mb-2 block text-sm font-semibold text-slate-900"}>{field.label}</label>
            {control}
            <p id={`${id}-help`} className={compactDetails ? "mt-1 text-xs leading-relaxed text-slate-500" : "mt-2 text-sm leading-relaxed text-slate-500"}>{field.help}</p>
            {fieldErrors[field.key] && <p id={`${id}-error`} className="mt-1 text-sm text-rose-700">{String(fieldErrors[field.key])}</p>}
          </div>;
        })}
      </fieldset>
      {error && <div role="alert" className="mt-6 rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}<button type="button" disabled={saving} className="ml-3 underline" onClick={() => { if (window.confirm("Reload and discard unsaved changes?")) setReload((value) => value + 1); }}>Reload settings</button></div>}
      <div className={compactDetails ? "mt-5 flex flex-wrap items-center justify-between gap-3" : "mt-8 flex items-center justify-between gap-4 border-t border-slate-200 pt-5"}>
        <p className={compactDetails ? "text-xs text-slate-500" : "text-sm text-slate-500"} role="status">{saving ? "Saving…" : saved ? "Settings saved." : ""}</p>
        <button disabled={saving} className="rounded-lg bg-violet-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:opacity-50">Save changes</button>
      </div>
    </form>
  );
}
